import { AssistantApiError, AssistantApiErrorCode } from '@src/types/assistant';

const errorBodyOf = (err: AssistantApiError): Record<string, unknown> =>
  err.body != null && typeof err.body === 'object'
    ? (err.body as Record<string, unknown>)
    : {};

/**
 * Turns a non-2xx answer of the assistant API into the `{ data, error }` shape
 * `b2chatBaseQuery` understands, keeping the status and the response body
 * (`message`, `error`, …) that `miniSerializeError` would otherwise drop. The
 * body is spread last so `error.message` is the API's text, not the generic
 * "HTTP error! Status: N". Anything else is rethrown untouched.
 */
export const assistantApiErrorResult = (err: unknown) => {
  if (err instanceof AssistantApiError) {
    return {
      data: null,
      error: { status: err.status, message: err.message, ...errorBodyOf(err) },
    };
  }
  throw err;
};

/**
 * Helper function to handle MCP connection API errors and extract error flags
 * @param err - The error caught from the API call
 * @returns An object with error details including isDuplicateName and isInvalidName flags
 */
export const handleMcpConnectionError = (err: unknown) => {
  if (err instanceof AssistantApiError) {
    const errorBody = errorBodyOf(err);

    // Detect duplicate name error
    const isDuplicateName =
      err.status === 409 &&
      errorBody.error === AssistantApiErrorCode.RESOURCE_ALREADY_EXISTS;

    // Detect invalid name format error
    const isInvalidName =
      err.status === 400 &&
      errorBody.error === 'BAD_REQUEST' &&
      typeof errorBody.message === 'string' &&
      errorBody.message.startsWith('name:');

    // Detect "at least 1 enabled tool" error (409 on activate)
    const isNoToolsSelected =
      err.status === 409 &&
      typeof errorBody.message === 'string' &&
      errorBody.message.includes('at least 1 enabled tool');

    return {
      data: null,
      error: {
        status: err.status,
        message: err.message,
        isDuplicateName,
        isInvalidName,
        isNoToolsSelected,
        ...errorBody,
      },
    };
  }
  throw err;
};
